import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Collapse, Alert, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ButtonOne from "../../common/normal-button";
import AppChip from "../../common/app-chip";
import AppTable from "../../common/app-table";
import { ColorsConstants, borderColors } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { PHARMACY_UPLOAD_BATCHES_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

const FILE_TYPES = [
  {
    key: "EMPLOYEE_MASTER",
    label: "Employee Master",
    entity: "Employee",
    columns: ["Employee_ID", "Employee_Name", "Employee_Phone_1", "Designation", "ABM Name", "Sub_Region", "ABM_HQ", "MN_HQ"],
  },
  {
    key: "EMPLOYEE_MASTER_MT",
    label: "Employee Master MT",
    entity: "Employee",
    columns: ["Employee_ID", "Employee_Name", "Employee_Phone_1", "Designation", "ABM Name", "Sub_Region", "ABM_HQ", "MN_HQ"],
  },
  {
    key: "CUSTOMER_MASTER",
    label: "Customer Master",
    entity: "Customer",
    columns: ["GSTIN", "Ecubix ID", "Customer_Name", "City", "State", "Employee Status", "Emp. ID", "MN HQ"],
  },
  {
    key: "CUSTOMER_MASTER_MT",
    label: "Customer Master MT",
    entity: "Customer",
    columns: ["Source", "Store ID", "Ecubix ID", "Customer_Name", "City", "State", "Employee Status", "Emp. ID", "MN HQ"],
  },
];

function buildResultMessage(batch, entityLabel) {
  const created = batch.created_count ?? 0;
  const updated = batch.updated_count ?? 0;
  if (created === 0 && updated === 0) return null;

  const plural = (n) => (n === 1 ? "record" : "records");

  if (updated > 0 && created === 0) {
    return `${entityLabel} records updated successfully.`;
  }
  if (created > 0 && updated === 0) {
    return `${entityLabel} records uploaded successfully.`;
  }
  return (
    `${entityLabel} records uploaded successfully. ` +
    `${updated} ${plural(updated)} updated and ${created} ${plural(created)} added.`
  );
}

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"];

const STATUS_META = {
  idle: { label: "No file selected", color: "default" },
  ready: { label: "Ready to upload", color: "default" },
  uploading: { label: "Uploading", color: "info", spinning: true },
  PENDING: { label: "Queued", color: "warning", spinning: true },
  PROCESSING: { label: "Processing", color: "warning", spinning: true },
  COMPLETED: { label: "Completed", color: "success", icon: <CheckCircleIcon fontSize="small" /> },
  COMPLETED_WITH_ERRORS: { label: "Completed with errors", color: "warning", icon: <WarningAmberIcon fontSize="small" /> },
  FAILED: { label: "Failed", color: "error", icon: <ErrorIcon fontSize="small" /> },
  error: { label: "Failed", color: "error", icon: <ErrorIcon fontSize="small" /> },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.idle;
  return (
    <AppChip
      label={meta.label}
      color={meta.color}
      variant="outlined"
      icon={meta.spinning ? <CircularProgress size={12} /> : meta.icon}
    />
  );
}

function UploadCard({ type, onUploaded }) {
  const { getValidToken } = useAuth();
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [batch, setBatch] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setStatus("error");
      setErrorMessage("Only .csv files are supported.");
      return;
    }
    setFile(f);
    setStatus("ready");
    setErrorMessage(null);
    setBatch(null);
  };

  const pollBatch = (id, token, tenant) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${PHARMACY_UPLOAD_BATCHES_ENDPOINT}${id}/`, {
          headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to fetch upload status.");

        setBatch(data);
        setStatus(data.status);

        if (TERMINAL_STATUSES.includes(data.status)) {
          clearInterval(pollRef.current);
          onUploaded?.();
        }
      } catch (err) {
        clearInterval(pollRef.current);
        setStatus("error");
        setErrorMessage(err.message || "Failed to fetch upload status.");
      }
    }, POLL_INTERVAL_MS);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus("uploading");
    setErrorMessage(null);
    setBatch(null);

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", type.key);

      const res = await fetch(PHARMACY_UPLOAD_BATCHES_ENDPOINT, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        const message = data.file?.[0] || data.file_type?.[0] || data.detail || "Upload failed.";
        setStatus("error");
        setErrorMessage(message);
        return;
      }

      setBatch(data);
      setStatus(data.status);
      pollBatch(data.id, token, tenant);
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message || "An unexpected error occurred.");
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${ColorsConstants.secondaryGray}`,
        borderRadius: 2,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        bgcolor: ColorsConstants.primaryWhite,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ ...typography.h2 }}>{type.label}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {type.columns.map((c) => (
              <Box
                key={c}
                sx={{
                  ...typography.bodySmall,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: ColorsConstants.primaryBlue60,
                  color: ColorsConstants.primaryGray60,
                  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                }}
              >
                {c}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `1px dashed ${ColorsConstants.inputFieldGrey}`,
          borderRadius: 1,
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: ColorsConstants.primaryBlue60,
            borderColor: ColorsConstants.primaryBackgroundColor,
          },
        }}
      >
        {file ? <InsertDriveFileIcon sx={{ color: ColorsConstants.primaryGray40 }} /> : <CloudUploadIcon sx={{ color: ColorsConstants.primaryGray40 }} />}
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>
          {file ? file.name : "Drag file here or click to browse"}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <StatusBadge status={status} />
        <ButtonOne
          title="Upload"
          variant="contained"
          disabled={!file || status === "uploading" || status === "PENDING" || status === "PROCESSING"}
          onClick={handleUpload}
        />
      </Box>

      {batch && (
        <Box sx={{ borderTop: `1px solid ${ColorsConstants.secondaryGray}`, pt: 1 }}>
          {buildResultMessage(batch, type.entity) && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.success, fontWeight: 600, mb: 0.5 }}>
              {buildResultMessage(batch, type.entity)}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}>
            <span>{batch.row_count} rows read</span>
            <span style={{ color: ColorsConstants.success }}>{batch.success_count} loaded</span>
            {batch.error_count > 0 && <span style={{ color: "#b45309" }}>{batch.error_count} errors</span>}
          </Box>
        </Box>
      )}

      {batch?.error_log?.length > 0 && (
        <Box sx={{ maxHeight: 160, overflowY: "auto", borderTop: `1px solid ${ColorsConstants.secondaryGray}`, pt: 1 }}>
          {batch.error_log.map((e, i) => (
            <Typography key={i} sx={{ ...typography.bodySmall, color: ColorsConstants.error, display: "block" }}>
              Row {e.row}: {e.error}
            </Typography>
          ))}
        </Box>
      )}

      <Collapse in={status === "error"}>
        <Alert severity="error" sx={{ ...typography.bodySmall }} onClose={() => setStatus(file ? "ready" : "idle")}>
          {errorMessage}
        </Alert>
      </Collapse>
    </Box>
  );
}

const HISTORY_COLUMNS = [
  { id: "id", label: "Batch ID" },
  { id: "file_type_display", label: "File Type" },
  { id: "file_name", label: "File Name" },
  { id: "uploaded_at", label: "Uploaded At", render: (r) => formatWithTzLabel(r.uploaded_at) },
  { id: "row_count", label: "Rows" },
  { id: "created_count", label: "Created" },
  { id: "updated_count", label: "Updated" },
  { id: "error_count", label: "Errors" },
  {
    id: "status", label: "Status",
    render: (r) => (
      <AppChip
        label={r.status_display}
        variant="outlined"
        color={
          r.status === "COMPLETED" ? "success"
          : r.status === "COMPLETED_WITH_ERRORS" ? "warning"
          : r.status === "FAILED" ? "error"
          : "default"
        }
      />
    ),
  },
];

function UploadHistory({ refreshKey }) {
  const { getValidToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
      const res = await fetch(`${PHARMACY_UPLOAD_BATCHES_ENDPOINT}?${params}`, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
      });
      const data = await res.json();
      if (!res.ok) throw new Error("Failed to fetch upload history.");
      setRows(data.results ?? []);
      setTotalCount(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching OTM upload history:", err);
    } finally {
      setLoading(false);
    }
  }, [getValidToken, page, rowsPerPage]);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, refreshKey]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ ...typography.h2, mb: 1.5 }}>Upload history</Typography>
      <AppTable
        columns={HISTORY_COLUMNS}
        rows={rows}
        rowKey="id"
        loading={loading}
        emptyMessage="No uploads yet"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </Box>
  );
}

export default function PharmacyUploadPanel({ onUploaded }) {
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const handleUploaded = () => {
    onUploaded?.();
    setHistoryRefreshKey((k) => k + 1);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        {FILE_TYPES.map((type) => (
          <UploadCard key={type.key} type={type} onUploaded={handleUploaded} />
        ))}
      </Box>

      <UploadHistory refreshKey={historyRefreshKey} />
    </Box>
  );
}
